import { useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import { LoadingOutlined } from '@ant-design/icons';
import { Divider, Popover, Spin } from 'antd';

import { OWNER_AVATAR_SIZES, VALIDATION_MESSAGES } from '@/shared/lib/constants';
import { formatDateTime } from '@/shared/lib/format';
import { BadgeList } from '@/shared/ui/BadgeList';
import { DomainEditPopover } from '@/shared/ui/DomainEditPopover';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { Icon } from '@/shared/ui/Icon';
import { IconButton } from '@/shared/ui/IconButton';
import { MetadataEditModal } from '@/shared/ui/MetadataEditModal';
import { MetadataItem } from '@/shared/ui/MetadataItem';
import type { MetadataItem as MetadataItemType } from '@/shared/ui/MetadataList';
import { OwnerAvatars } from '@/shared/ui/OwnerAvatars';
import { OwnerEditPopover } from '@/shared/ui/OwnerEditPopover';
import { SynonymEditPopover } from '@/shared/ui/SynonymEditPopover';

import { useDisplaySettingsStore } from '@/entities/display-settings';

import type { GlossaryTermDetail } from '@/features/glossary/api';
import { useGlossaryTermDomainEdit } from '@/features/glossary/model/useGlossaryTermDomainEdit';
import { useGlossaryTermOwnerEdit } from '@/features/glossary/model/useGlossaryTermOwnerEdit';
import { useGlossaryTermRelatedTermEdit } from '@/features/glossary/model/useGlossaryTermRelatedTermEdit';
import { useGlossaryTermSynonymEdit } from '@/features/glossary/model/useGlossaryTermSynonymEdit';
import { useGlossaryTermTagEdit } from '@/features/glossary/model/useGlossaryTermTagEdit';

import styles from './GlossaryTermSummaryCard.module.css';

type GlossaryTermSummaryCardProps = {
  termData: GlossaryTermDetail;
  fullTermFqn: string;
  termCategory: string;
};

export const GlossaryTermSummaryCard = ({ termData, fullTermFqn, termCategory }: GlossaryTermSummaryCardProps) => {
  const navigate = useNavigate();

  const domainEdit = useGlossaryTermDomainEdit({
    termId: termData.id,
    termFqn: fullTermFqn,
    termCategory,
  });

  const domainItems: MetadataItemType[] = useMemo(
    () =>
      (termData.domains || []).map((domain) => ({
        id: domain.id,
        label: domain.displayName || domain.name,
        onClick: (e?: React.MouseEvent | React.KeyboardEvent | string) => {
          if (typeof e !== 'string') {
            e?.stopPropagation();
          }
          navigate(`/domain/${encodeURIComponent(domain.fullyQualifiedName)}/sub-domains`);
        },
      })),
    [termData.domains, navigate]
  );

  const renderDomainSection = (
    <MetadataItem title="도메인" icon={<Icon name="globe" size={14} />}>
      <div className={styles.metadataItemSpace}>
        <div className={styles.metadataContent}>
          {domainEdit.isUpdating ? (
            <Spin indicator={<LoadingOutlined spin />} size="small" />
          ) : domainItems.length > 0 ? (
            <EllipsisText text={domainItems.map((item) => item.label).join(', ')} />
          ) : (
            '–'
          )}
        </div>
        <Popover
          content={<DomainEditPopover domainEdit={domainEdit} />}
          trigger="click"
          open={domainEdit.isOpen}
          onOpenChange={(visible) => (visible ? domainEdit.handleOpen() : domainEdit.handleClose())}
          placement="bottomLeft">
          <IconButton size="small" tooltip="도메인 편집">
            <Icon name="edit" size={16} />
          </IconButton>
        </Popover>
      </div>
    </MetadataItem>
  );

  const showOwners = useDisplaySettingsStore((state) => state.showOwners);

  const ownerEdit = useGlossaryTermOwnerEdit({
    termId: termData.id,
    termFqn: fullTermFqn,
  });

  const renderOwnerSection = showOwners && (
    <>
      <Divider type="vertical" className={styles.metadataDivider} />
      <MetadataItem title="소유자">
        <div className={styles.metadataItemSpace}>
          <div className={styles.metadataContent}>
            {termData.owners && termData.owners.length > 0 ? (
              <OwnerAvatars owners={termData.owners} size={OWNER_AVATAR_SIZES.DETAIL} showName={true} />
            ) : (
              '–'
            )}
          </div>
          <Popover
            content={<OwnerEditPopover ownerEdit={ownerEdit} />}
            title="소유자 편집"
            trigger="click"
            open={ownerEdit.isOpen}
            onOpenChange={(visible) => (visible ? ownerEdit.handleOpen() : ownerEdit.handleClose())}
            placement="bottomLeft">
            <IconButton size="small" tooltip="소유자 편집">
              <Icon name="edit" size={16} />
            </IconButton>
          </Popover>
        </div>
      </MetadataItem>
    </>
  );

  const synonymEdit = useGlossaryTermSynonymEdit({
    termId: termData.id,
    termFqn: fullTermFqn,
  });

  const renderSynonymSection = (
    <MetadataItem title="동의어" icon={<Icon name="book" size={14} />}>
      <div className={styles.metadataItemSpace}>
        <div className={styles.metadataContent}>
          {termData.synonyms && termData.synonyms.length > 0 ? (
            <BadgeList
              items={[...termData.synonyms].map((synonym, index) => ({
                tagFQN: `synonym-${index}`,
                name: synonym,
                displayName: synonym,
              }))}
              iconName="book"
              maxVisibleCount={1}
            />
          ) : (
            '–'
          )}
        </div>
        <Popover
          className={styles.popover}
          content={<SynonymEditPopover synonymEdit={synonymEdit} />}
          trigger="click"
          open={synonymEdit.isOpen}
          onOpenChange={(visible) => (visible ? synonymEdit.handleOpen() : synonymEdit.handleClose())}
          placement="bottomLeft">
          <IconButton size="small" tooltip="동의어 편집">
            <Icon name="edit" size={16} />
          </IconButton>
        </Popover>
      </div>
    </MetadataItem>
  );

  const relatedTermEdit = useGlossaryTermRelatedTermEdit({
    termId: termData.id,
    termFqn: fullTermFqn,
  });

  const renderRelatedTermSection = (
    <MetadataItem title="관련 용어" icon={<Icon name="book" size={14} />}>
      <div className={styles.metadataItemSpace}>
        <div className={styles.metadataContent}>
          {relatedTermEdit.isUpdating || relatedTermEdit.isLoadingTerms ? (
            <Spin indicator={<LoadingOutlined spin />} size="small" />
          ) : relatedTermEdit.relatedBadgeItems.length > 0 ? (
            <BadgeList
              items={relatedTermEdit.relatedBadgeItems}
              onItemClick={(fqn) => {
                const item = relatedTermEdit.relatedBadgeItems.find((t) => t.tagFQN === fqn);
                if (item) {
                  item?.onClick?.();
                }
              }}
              iconName="book"
              maxVisibleCount={1}
            />
          ) : (
            '–'
          )}
        </div>
        <IconButton size="small" onClick={() => relatedTermEdit.handleOpen()} tooltip="관련 용어 편집">
          <Icon name="edit" size={16} />
        </IconButton>
        <MetadataEditModal
          checkStrictly={true}
          open={relatedTermEdit.isOpen}
          icon={<Icon name="book" size={12} />}
          title="관련 용어 편집"
          firstLabel="선택된 관련 용어"
          secondLabel="관련 용어"
          placeholder="관련 용어를 검색하세요"
          searchValidation={{
            maxLength: 256,
            warningMessage: VALIDATION_MESSAGES.GLOSSARY_SEARCH_MAX_LENGTH,
          }}
          loading={relatedTermEdit.isUpdating || relatedTermEdit.isLoadingTerms}
          treeData={relatedTermEdit.relatedTermTreeData}
          treeSelectValue={relatedTermEdit.relatedTermTreeSelectValue}
          onClose={() => relatedTermEdit.handleClose()}
          onSubmit={relatedTermEdit.handleSubmit}
        />
      </div>
    </MetadataItem>
  );

  const tagEdit = useGlossaryTermTagEdit({
    termId: termData.id,
    termFqn: fullTermFqn,
  });

  const renderTagSection = (
    <MetadataItem title="태그" icon={<Icon name="tag" size={14} />}>
      <div className={styles.metadataItemSpace}>
        <div className={styles.metadataContent}>
          {tagEdit.isUpdating || tagEdit.isLoadingTags ? (
            <Spin indicator={<LoadingOutlined spin />} size="small" />
          ) : tagEdit.tagBadgeItems.length > 0 ? (
            <BadgeList
              items={tagEdit.tagBadgeItems}
              onItemClick={(fqn) => {
                const item = tagEdit.tagBadgeItems.find((t) => t.tagFQN === fqn);
                if (item) {
                  item?.onClick?.();
                }
              }}
              maxVisibleCount={1}
            />
          ) : (
            '–'
          )}
        </div>
        <IconButton size="small" onClick={() => tagEdit.handleOpen()} tooltip="태그 편집">
          <Icon name="edit" size={16} />
        </IconButton>
        <MetadataEditModal
          open={tagEdit.isOpen}
          icon={<Icon name="tag" size={12} />}
          title="태그 편집"
          firstLabel="선택된 태그"
          secondLabel="태그"
          placeholder="태그를 검색하세요"
          searchValidation={{
            maxLength: 30,
            warningMessage: VALIDATION_MESSAGES.TAG_SEARCH_MAX_LENGTH,
          }}
          loading={tagEdit.isUpdating || tagEdit.isLoadingTags}
          treeData={tagEdit.tagTreeData}
          treeSelectValue={tagEdit.tagTreeSelectValue}
          onClose={() => tagEdit.handleClose()}
          onSubmit={tagEdit.handleSubmit}
        />
      </div>
    </MetadataItem>
  );

  return (
    <div className={styles.summaryCard}>
      <div className={styles.metadata}>
        {renderDomainSection}
        {renderOwnerSection}
        <Divider type="vertical" className={styles.metadataDivider} />
        {renderSynonymSection}

        <Divider type="vertical" className={styles.metadataDivider} />
        {renderRelatedTermSection}

        <Divider type="vertical" className={styles.metadataDivider} />
        {renderTagSection}

        <Divider type="vertical" className={styles.metadataDivider} />
        <MetadataItem title="수정일">
          <span className={styles.itemName}>{formatDateTime(termData.updatedAt)}</span>
        </MetadataItem>
      </div>
    </div>
  );
};
